import { DatePicker } from "antd";
import {
  CalendarOutlined,
  DoubleLeftOutlined,
  AimOutlined,
} from "@ant-design/icons";
import { ViewMode } from "@thepocman/gantt-task-react";
import dayjs, { type Dayjs } from "dayjs";
import TimelineControlButton from "./TimelineControlButton";
import "./timeline-header-controls.css";

type TimelineHeaderControlsProps = {
  viewMode: ViewMode;
  selectedDate: Dayjs | null;
  isTodayActive: boolean;
  isFirstEventActive: boolean;
  hasFirstEventDate: boolean;
  minSelectableDate: Date | null;
  maxSelectableDate: Date | null;
  testIdPrefix: string;
  onViewModeChange: (viewMode: ViewMode) => void;
  onDateChange: (date: Dayjs | null) => void;
  onTodayClick: () => void;
  onEarliestClick: () => void;
};

const VIEW_MODE_BUTTONS = [
  { label: "Years", value: ViewMode.Year, suffix: "years" },
  { label: "Months", value: ViewMode.Month, suffix: "months" },
  { label: "Days", value: ViewMode.Day, suffix: "days" },
];

const DATE_PICKER_CONFIG = {
  [ViewMode.Day]: {
    picker: "date" as const,
    format: "MM/DD/YYYY",
    placeholder: "Select date",
  },
  [ViewMode.Month]: {
    picker: "month" as const,
    format: "MM/YYYY",
    placeholder: "Select month",
  },
  [ViewMode.Year]: {
    picker: "year" as const,
    format: "YYYY",
    placeholder: "Select year",
  },
};

export default function TimelineHeaderControls({
  viewMode,
  selectedDate,
  isTodayActive,
  isFirstEventActive,
  hasFirstEventDate,
  minSelectableDate,
  maxSelectableDate,
  testIdPrefix,
  onViewModeChange,
  onDateChange,
  onTodayClick,
  onEarliestClick,
}: TimelineHeaderControlsProps) {
  const datePickerConfig =
    DATE_PICKER_CONFIG[viewMode as keyof typeof DATE_PICKER_CONFIG] ??
    DATE_PICKER_CONFIG[ViewMode.Day];
  const minDate = minSelectableDate ? dayjs(minSelectableDate) : null;
  const maxDate = maxSelectableDate ? dayjs(maxSelectableDate) : null;

  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap items-center gap-2">
        {VIEW_MODE_BUTTONS.map(({ label, value, suffix }) => (
          <TimelineControlButton
            key={value}
            label={label}
            testId={`${testIdPrefix}-view-button-${suffix}`}
            isActive={viewMode === value}
            variant="tab"
            onClick={() => onViewModeChange(value)}
          />
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-1">
        {hasFirstEventDate ? (
          <TimelineControlButton
            label="Earliest"
            testId={`${testIdPrefix}-view-button-earliest-date`}
            isActive={isFirstEventActive}
            variant="secondary"
            icon={<DoubleLeftOutlined />}
            tooltip="Jumps to the earliest date horizontally."
            onClick={onEarliestClick}
          />
        ) : null}

        <DatePicker
          picker={datePickerConfig.picker}
          value={selectedDate}
          onChange={onDateChange}
          placeholder={datePickerConfig.placeholder}
          format={datePickerConfig.format}
          allowClear={false}
          minDate={minDate ?? undefined}
          maxDate={maxDate ?? undefined}
          suffixIcon={<CalendarOutlined />}
          className="timeline-header-date-picker w-[121px]"
          data-testid={`${testIdPrefix}-view-date-picker`}
        />

        <TimelineControlButton
          label="Today"
          testId={`${testIdPrefix}-view-button-today`}
          isActive={isTodayActive}
          variant="secondary"
          icon={<AimOutlined />}
          onClick={onTodayClick}
        />
      </div>
    </div>
  );
}
